import type { GetMyPassengerResponse } from '@massilia/queries'

import {
  useDeleteMyFidelityCardMutation,
  useDeleteMyIdentityDocumentMutation,
  useDeleteMyPassengerMutation,
} from '@massilia/queries'
import { Button, Name, Separator } from '@massilia/ui'
import { format } from 'date-fns'
import { CreditCard, Fingerprint, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'

import { GENDER_LABELS_FULL } from '@/utils/gender-labels'

import { FidelityCardForm } from './fidelity-card-form'
import { IdentityDocumentForm } from './identity-document-form'

interface PassengerCardProps {
  onEdit: (passenger: GetMyPassengerResponse) => void
  passenger: GetMyPassengerResponse
}

export function PassengerCard({ onEdit, passenger }: PassengerCardProps) {
  const [showIdentityForm, setShowIdentityForm] = useState(false)
  const [showFidelityForm, setShowFidelityForm] = useState(false)

  const { mutate: deletePassenger } = useDeleteMyPassengerMutation()
  const { mutate: deleteIdentityDocument } = useDeleteMyIdentityDocumentMutation()
  const { mutate: deleteFidelityCard } = useDeleteMyFidelityCardMutation()

  const docTypeLabels: Record<string, string> = {
    CNI: "Carte d'identité",
    PASSPORT: 'Passeport',
  }

  return (
    <div className="border-gray-light-200 rounded-xl border bg-white">
      {/* Header */}
      <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-brand-600 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white">
            {passenger.firstName.charAt(0)}
            {passenger.lastName.charAt(0)}
          </div>
          <div>
            <h4 className="text-sm font-semibold text-slate-800">
              {GENDER_LABELS_FULL[passenger.gender] ?? ''}{' '}
              <Name>
                {passenger.firstName} <span className="uppercase">{passenger.lastName}</span>
              </Name>
            </h4>
            <p className="text-xs text-slate-500">
              Né(e) le {format(new Date(passenger.birthDate), 'dd/MM/yyyy')}
              {passenger.nationalityRef && <> &middot; {passenger.nationalityRef.nationalityFr}</>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 self-end sm:self-auto">
          <Button
            className="h-7 gap-1 text-xs"
            onClick={() => onEdit(passenger)}
            size="sm"
            variant="ghost"
          >
            <Pencil className="size-3.5" />
            Modifier
          </Button>
          <Button
            className="h-7 text-xs text-red-600 hover:text-red-700"
            onClick={() => deletePassenger(passenger.id)}
            size="sm"
            variant="ghost"
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      <Separator />

      {/* Identity documents */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">Documents d&apos;identité</span>
          <button
            className="text-brand-600 hover:text-brand-700 flex items-center gap-1 text-xs font-medium"
            onClick={() => setShowIdentityForm(true)}
            type="button"
          >
            <Plus className="size-3" />
            Ajouter
          </button>
        </div>

        {passenger.identityDocuments.length > 0 ? (
          <div className="mt-2 space-y-2">
            {passenger.identityDocuments.map((doc) => (
              <div key={doc.id} className="flex flex-col gap-1 rounded-lg bg-slate-50 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Fingerprint className="size-3.5 shrink-0 text-slate-400" />
                    <span className="truncate text-xs font-medium text-slate-700">
                      {docTypeLabels[doc.type] ?? doc.type}
                    </span>
                    <span className="truncate font-mono text-xs text-slate-600">
                      {doc.documentNumber}
                    </span>
                  </div>
                  <Button
                    className="h-5 w-5 shrink-0 text-red-500 hover:text-red-700"
                    onClick={() =>
                      deleteIdentityDocument({
                        documentId: doc.id,
                        passengerId: passenger.id,
                      })
                    }
                    size="sm"
                    variant="ghost"
                  >
                    <Trash2 className="size-3" />
                  </Button>
                </div>
                <span className="text-xs text-slate-400">
                  Exp. {format(new Date(doc.expiryDate), 'dd/MM/yyyy')}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-xs text-slate-400">Aucun document</p>
        )}

        {showIdentityForm && (
          <IdentityDocumentForm
            onClose={() => setShowIdentityForm(false)}
            passengerId={passenger.id}
          />
        )}
      </div>

      <Separator />

      {/* Fidelity cards */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-slate-500">Cartes de fidélité</span>
          <button
            className="text-brand-600 hover:text-brand-700 flex items-center gap-1 text-xs font-medium"
            onClick={() => setShowFidelityForm(true)}
            type="button"
          >
            <Plus className="size-3" />
            Ajouter
          </button>
        </div>

        {passenger.fidelityCards.length > 0 ? (
          <div className="mt-2 space-y-2">
            {passenger.fidelityCards.map((card) => (
              <div
                key={card.id}
                className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2"
              >
                <div className="flex min-w-0 items-center gap-2">
                  <CreditCard className="size-3.5 shrink-0 text-slate-400" />
                  <div className="min-w-0">
                    <span className="text-xs font-medium text-slate-700">
                      {card.companyName || card.iataCode}
                    </span>
                    <span className="block font-mono text-xs text-slate-600 sm:ml-2 sm:inline">
                      {card.cardNumber}
                    </span>
                  </div>
                </div>
                <Button
                  className="h-5 w-5 shrink-0 text-red-500 hover:text-red-700"
                  onClick={() =>
                    deleteFidelityCard({
                      cardId: card.id,
                      passengerId: passenger.id,
                    })
                  }
                  size="sm"
                  variant="ghost"
                >
                  <Trash2 className="size-3" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-xs text-slate-400">Aucune carte</p>
        )}

        {showFidelityForm && (
          <FidelityCardForm onClose={() => setShowFidelityForm(false)} passengerId={passenger.id} />
        )}
      </div>
    </div>
  )
}
